import { useEffect, useMemo } from "react";
import { Resolver, useForm } from "react-hook-form";
import useEvent from "react-use-event-hook";
import { useRecoilValue } from "recoil";

import { useSchema, useYupValidationResolver } from "@ryde/redux";
import {
  collectAdditionValueBulkControlsPreservingTabOrder,
  controlsSF,
  createErrorResult,
  createSuccessResult,
  DrawerType,
  errors as errorsKeys,
  getBulkBasePriceAdditionToggleFieldName,
  ICustomField,
  IRydeProcess,
  isSchemaRootLoadedSF,
  PriceRateApi,
  RydeAppUnit,
  RydeProcessTitle,
  selectedIdsSF,
  SimpleResult,
  tabSF,
  useDrawers,
} from "@ryde/shared";

import {
  buildUpdateBasePriceDefaultValues,
  buildUpdateBasePriceValidationSchema,
  IUpdateBasePriceForm,
} from "./update-base-price-drawer.models";

const PRICE_RATE_ENTITY: RydeAppUnit = "priceRate" as const;

export const useUpdateBasePriceDrawer = () => {
  const selectedIds = useRecoilValue(selectedIdsSF(PRICE_RATE_ENTITY));
  const { closeDrawer, openDrawer } = useDrawers();
  const controls = useRecoilValue(controlsSF(PRICE_RATE_ENTITY));
  const tabs = useRecoilValue(tabSF(PRICE_RATE_ENTITY));
  const isSchemaLoaded = useRecoilValue(
    isSchemaRootLoadedSF(PRICE_RATE_ENTITY),
  );
  const { fetchSchema } = useSchema(PRICE_RATE_ENTITY);

  const additionControls: ICustomField[] = useMemo(
    () =>
      collectAdditionValueBulkControlsPreservingTabOrder({ controls, tabs }),
    [controls, tabs],
  );

  const validationSchema = useMemo(
    () => buildUpdateBasePriceValidationSchema(additionControls),
    [additionControls],
  );

  const resolver = useYupValidationResolver(
    validationSchema,
    PRICE_RATE_ENTITY,
  );

  const methods = useForm<IUpdateBasePriceForm>({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "firstError",
    defaultValues: buildUpdateBasePriceDefaultValues([]),
    resolver: resolver as Resolver<IUpdateBasePriceForm>,
  });

  const {
    reset,
    getValues,
    formState: { errors, isValid, touchedFields },
  } = methods;

  useEffect(() => {
    if (!isSchemaLoaded) {
      fetchSchema();
    }
  }, [fetchSchema, isSchemaLoaded]);

  useEffect(() => {
    reset(buildUpdateBasePriceDefaultValues(additionControls));
  }, [additionControls, reset]);

  const isSaveDisabled = Boolean(
    !isValid ||
    Object.keys(errors || {}).length ||
    !touchedFields.percentageOfIncrease,
  );

  const onSaveClick = useEvent(async (): Promise<SimpleResult<void>> => {
    if (!isValid) {
      return createErrorResult(errorsKeys.common.validationError);
    }

    const values = getValues() as IUpdateBasePriceForm &
      Record<string, boolean>;

    const currencyAdditionKeysToIncrease = additionControls
      .filter(
        control =>
          values[getBulkBasePriceAdditionToggleFieldName(control.name)],
      )
      .map(control => control.name);

    const priceRatesUpdated = await PriceRateApi.updateBasePrice({
      percentageOfIncrease: values.percentageOfIncrease,
      roundFinalPrice: values.roundFinalPrice,
      currencyAdditionKeysToIncrease,
      ids: selectedIds,
    });

    if (!priceRatesUpdated) {
      return createErrorResult(errorsKeys.common.errorSavingToAPI);
    }

    if (priceRatesUpdated?.success) {
      const resultData = priceRatesUpdated.data as IRydeProcess;
      closeDrawer(DrawerType.UpdateBasePrice);
      openDrawer<DrawerType.ProcessDrawer>({
        drawerProps: {
          type: DrawerType.ProcessDrawer,
          processId: resultData.id,
          processTitle: RydeProcessTitle.BulkUpdateBasePrice,
          entity: PRICE_RATE_ENTITY,
        },
      });
    }

    return createSuccessResult();
  });

  return {
    methods,
    errors,
    isSaveDisabled,
    onSaveClick,
    additionControls,
    isAdditionSchemaReady: isSchemaLoaded,
  };
};
